import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import Toaster from '../components/toaster';
import ModalController from '../components/modal';
import BackendDrivenToaster from './backendDrivenToaster';
import { GlobalLoading } from '../components/loaders/globalLoading';
import SideMenu from '../components/sideMenu';
import SideMenuMobile from '../components/sideMenu/sideMenuMobile';
import rest from '../data/restWrapper';
import Licence from './licence';

const MasterLayout = (props) => {
    const { fixedRoute, authTokenInfo, children } = props
    const location = useLocation();
    const myRef = useRef(null);

    const [mobileView, setMobileView] = useState(window.innerWidth <= 600);
    const [licenceMessage, setLicenceMessage] = useState(null);
    const [paymentMessage, setPaymentMessage] = useState(null);
    const [isSoftwareBlockedDueToLicenseExpiry, setIsSoftwareBlockedDueToLicenseExpiry] = useState(false);
    const [preExpiryPeriod, setPreExpiryPeriod] = useState(false);
    const [, setIsPaymentOverdue] = useState(false);

    // Get page width
    const getWidth = () =>
        Math.max(
            document.body.scrollWidth,
            document.documentElement.scrollWidth,
            document.body.offsetWidth,
            document.documentElement.offsetWidth,
            document.documentElement.clientWidth
        );

    // Fetch license details
    const getLicenceDetails = useCallback(async () => {
        try {
            const licenceDetails = await rest('/licenceDetails');
            if (licenceDetails) {
                setLicenceMessage(licenceDetails.message);
                setPaymentMessage(licenceDetails.paymentMessage);
                setIsSoftwareBlockedDueToLicenseExpiry(licenceDetails.isSoftwareBlockedDueToLicenseExpiry);
                setPreExpiryPeriod(licenceDetails.preExpiryPeriod);
                setIsPaymentOverdue(licenceDetails.isPaymentOverdue);
            }
        } catch (error) {
            console.error('Error fetching licence details:', error);
        }
    }, []);

    // Component did mount
    useEffect(() => {
        const init = async () => {
            try {
                setMobileView(getWidth() <= 600);

                if (process.env.NODE_ENV === 'development') {
                    window.addEventListener('error', (e) => {
                        if (e.message === 'ResizeObserver loop limit exceeded') {
                            document
                                .getElementById('webpack-dev-server-client-overlay')
                                ?.setAttribute('style', 'display: none');
                            document
                                .getElementById('webpack-dev-server-client-overlay-div')
                                ?.setAttribute('style', 'display: none');
                        }
                    });
                }

                await getLicenceDetails();
            } catch (error) {
                console.error(error);
            }
        };

        init();
    }, [fixedRoute, getLicenceDetails]);

    // Scroll to top on route change
    useEffect(() => {
        if (myRef.current) {
            myRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }, [location.pathname]);
    
    // Redirects and different views
    if (
        location.pathname.includes('/invoiceView') ||
        location.pathname.includes('/proformaView') ||
        location.pathname.includes('/printPreview') ||
        location.pathname.includes('/cashRegReportView') ||
        location.search.includes('printJournal') ||
        location.pathname.includes('/billingSummaryInvoiceView') ||
        location.pathname.lastIndexOf('/invoiceConclusionView') !== -1
    ) {
        return (
            <div>
                <ModalController />
                <GlobalLoading />
                {children}
                <div className="d-print-none">
                    <Toaster />
                </div>
                <BackendDrivenToaster />
            </div>
        );
    }

    if (location.pathname.startsWith('/documentScanner')) {
        return (
            <div>
                <div className="docScan-bg docScanner-background">{children}</div>
                <div className="d-print-none">
                    <Toaster />
                </div>
                <BackendDrivenToaster />
            </div>
        );
    }

    return (
        <div ref={myRef} id={mobileView ? 'master-layout-mobile' : 'master-layout'}>
            <GlobalLoading />
            <ModalController />
            <div className="reception-background w-100">
                <div className="d-flex h-100 app-flex-div">
                    <SideMenu {...props} isSoftwareBlockedDueToLicenseExpiry={isSoftwareBlockedDueToLicenseExpiry} />
                    <div className="scrollX" id="main-app-div" style={{ width: '100%' }}>
                        {!authTokenInfo?.user?.role ? (
                            <div>Insufficient permissions</div>
                        ) : (
                            <>
                                {paymentMessage && (
                                    <div className={isSoftwareBlockedDueToLicenseExpiry ? 'licenceWarningExpiredPeriod' : 'licenceWarningPaymentOverdue'}>
                                        {paymentMessage}
                                    </div>
                                )}
                                {licenceMessage && (
                                    <div
                                        className={
                                            preExpiryPeriod && !isSoftwareBlockedDueToLicenseExpiry
                                                ? 'licenceWarningPreExpiredPeriod'
                                                : 'licenceWarningExpiredPeriod'
                                        }
                                    >
                                        {licenceMessage}
                                    </div>
                                )}
                                {!isSoftwareBlockedDueToLicenseExpiry ? children : <Licence />}
                            </>
                        )}
                    </div>
                    <div className="master-layout-clear clear pt-2">&ensp;</div>
                </div>
            </div>

            <div className="d-print-none">
                <Toaster />
            </div>
            <BackendDrivenToaster />
            <SideMenuMobile {...props} />
        </div>
    );
};

export default MasterLayout;
